<template>
  <div ref="root" class="sp-text-field" :class="classModifiers">
    <div class="sp-text-field-content">
      <div class="sp-text-field-prepend" @click="onPrependClick">
        <slot name="prepend">
          <sp-icon v-if="prependIcon" :name="prependIcon" class="sp-text-field-content__icon" part="prepend-icon" />
        </slot>
      </div>
      <div class="sp-text-field-content__input-container">
        <slot />
        <input
          ref="input"
          class="sp-text-field-content__input-field"
          :type="type"
          :value="model"
          :placeholder="placeholder"
          :disabled="disabled"
          :readonly="readonly"
          :inputmode="inputmode"
          :required="required"
          @input.stop.prevent="onInput"
          @focus="onFocus"
          @blur="onBlur"
        />
      </div>
      <div class="sp-text-field-append-container">
        <div v-if="isClearVisible" class="sp-text-field-clear" @click.stop="onClearClick">
          <slot name="clear" class="clear-slot">
            <sp-button class="clear-button" size="small" color="surface" :icon="clearIcon" />
          </slot>
        </div>
        <div class="sp-text-field-append" @click="onAppendClick">
          <slot name="append" class="append-slot">
            <sp-icon v-if="appendIcon" :name="appendIcon" class="sp-text-field-content__icon" part="append-icon" />
          </slot>
        </div>
      </div>
    </div>

    <sp-linear-progress-bar v-if="loading" class="sp-text-field__loader" />
  </div>
</template>

<script>
import { useElementHover } from "@vueuse/core";
import { props as propsDefinitions } from "./props.js";
</script>

<script setup>
import { computed, ref, watch } from "vue";
import { useBreakpoints } from "../../composables/breakpoints";
import { useExpose } from "../../composables/expose";
import { toBoolean } from "../../utils/props";

const emit = defineEmits(["input", "click-append", "click-prepend", "click-clear", "input-initialized"]);

const props = defineProps({
  ...propsDefinitions,
  /**
   * Whether to propagate the click event on the append slot.
   * @type {Boolean}
   * @default false
   */
  propagateAppendClick: {
    type: [Boolean, String],
    default: false,
  },
});

/**
 * The root element of the component.
 * @type {import('vue').Ref<HTMLElement|null>}
 */
const root = ref(null);
const isHovered = useElementHover(root);

/**
 * The input element of the component.
 * @type {import('vue').Ref<HTMLInputElement|null>}
 */
const input = ref(null);

const { exposeProperties, exposeMethods } = useExpose(root);

exposeProperties({
  inputElement: {
    get: () => input.value,
  },
});

exposeMethods({
  reportValidity,
});

const loading = computed(() => toBoolean(props.loading));
const disabled = computed(() => toBoolean(props.disabled));
const readonly = computed(() => toBoolean(props.readonly));
const required = computed(() => toBoolean(props.required));

const propagateAppendClick = computed(() => toBoolean(props.propagateAppendClick));

const clearable = computed(() => toBoolean(props.clearable));
const persistentClear = computed(() => toBoolean(props.persistentClear));

const model = ref(props.value);
watch(model, () => emit("input", model.value));
watch(
  () => props.value,
  (value) => (model.value = value),
);

function onInput(event) {
  model.value = event.target.value;
}

const isFocused = ref(false);
function onFocus() {
  isFocused.value = true;
}

function onBlur() {
  isFocused.value = false;
}

const isClearVisible = computed(() => {
  // If the field is not clearable or the model is empty, we don't show the clear button.
  const shouldShowClear = clearable.value && model.value?.length > 0;

  // Return early if we don't need to show the clear button.
  if (!shouldShowClear) {
    return false;
  }

  // If the field is persistent clear, we always show the clear button. Otherwise, we show it when hovered.
  return persistentClear.value || isHovered.value;
});

function reportValidity() {
  input.value.setCustomValidity("");
  const isValid = input.value.validity.valid;

  if (props.customValidity && !isValid) {
    input.value.setCustomValidity(props.customValidity);
  }

  input.value.reportValidity();
  return isValid;
}

function onPrependClick() {
  emit("click-prepend");
}

function onAppendClick(e) {
  if (!propagateAppendClick.value) {
    e.stopPropagation();
  }
  emit("click-append");
}

function onClearClick() {
  model.value = undefined;
  emit("click-clear");
}

const { isSmScreen } = useBreakpoints(root);

const classModifiers = computed(() => [
  {
    "--readonly": readonly.value,
    "--is-focused": isFocused.value,
    "--is-invalid": false, // TODO @sleistner: Implement validation
    "--density-compact": isSmScreen.value,
    [`--density-${props.density}`]: props.density !== "default",
    [`--variant-${props.variant}`]: props.variant !== "default",
  },
]);
</script>

<style lang="scss">
:host {
  display: block;
  box-sizing: border-box;
}
:host(:focus) {
  outline: none !important;
  box-shadow: none !important;
  border: none !important;
}

slot[name="clear"] {
  display: flex;
  align-items: center;
  justify-content: center;
}
</style>

<style lang="scss" scoped>
.clear-button::part(icon) {
  --fill: v-bind(clearIconFill);
}
.sp-text-field {
  --border-color: var(--sp-comp-text-field-border-color, var(--sp-sys-form-field-border-color, #ccc));
  --border-width: var(--sp-comp-text-field-border-width, var(--sp-sys-form-field-border-width, 1px));
  --text-field-default-height: var(--sp-comp-text-field-min-height, var(--sp-sys-form-field-min-height, 3rem));
  --text-field-height: var(--text-field-default-height);
  --text-field-default-padding-inline: var(
    --sp-comp-text-field-padding-inline,
    var(--sp-sys-form-field-padding-inline, 1rem)
  );
  --text-field-padding-inline: var(--text-field-default-padding-inline);
  --text-field-default-padding-block: var(
    --sp-comp-text-field-padding-block,
    var(--sp-sys-form-field-padding-block, 0)
  );
  --text-field-padding-block: var(--text-field-default-padding-block);

  border-width: var(--border-width);
  border-style: var(--sp-comp-text-field-border-style, var(--sp-sys-form-field-border-style, solid));
  border-color: var(--border-color);
  border-radius: var(--sp-comp-text-field-border-radius, var(--sp-sys-form-field-border-radius, 0.3rem));
  box-sizing: border-box;
  outline: none;
  outline-offset: var(--sp-comp-text-field-outline-offset, var(--sp-sys-form-field-outline-offset, 0));
  overflow: hidden;
  position: relative;
  width: 100%;
  min-width: var(--sp-comp-text-field-min-width, 10ch);
  height: var(--text-field-height);
  background-color: var(--sp-comp-text-field-background-color, var(--sp-sys-form-field-background-color, #fff));

  &.--is-focused {
    --border-color: var(
      --sp-comp-text-field-focus-border-color,
      var(--sp-sys-form-field-focus-border-color, var(--sp-sys-color-primary))
    );
    box-shadow: var(--sp-comp-text-field-focus-box-shadow, var(--sp-sys-form-field-focus-box-shadow, none));
    outline: var(--sp-comp-text-field-focus-outline, var(--sp-sys-form-field-focus-outline, none));
    outline-offset: var(--sp-comp-text-field-focus-outline-offset, var(--sp-sys-form-field-focus-outline-offset, 0));
  }

  &.--is-invalid {
    --border-color: var(
      --sp-comp-text-field-invalid-border-color,
      var(--sp-sys-form-field-invalid-border-color, var(--sp-sys-color-error))
    );
    box-shadow: var(--sp-comp-text-field-invalid-box-shadow, var(--sp-sys-form-field-invalid-box-shadow, none));
    outline: var(--sp-comp-text-field-invalid-outline, var(--sp-sys-form-field-invalid-outline, none));
    outline-offset: var(
      --sp-comp-text-field-invalid-outline-offset,
      var(--sp-sys-form-field-invalid-outline-offset, 0)
    );
  }

  &.--density-compact {
    --text-field-height: var(
      --sp-comp-text-field-compact-min-height,
      var(--sp-sys-form-field-compact-min-height, var(--text-field-default-height))
    );
    --text-field-padding-inline: var(
      --sp-comp-text-field-compact-padding-inline,
      var(--sp-sys-form-field-compact-padding-inline, var(--text-field-default-padding-inline))
    );
    --text-field-padding-block: var(
      --sp-comp-text-field-compact-padding-block,
      var(--sp-sys-form-field-compact-padding-block, var(--text-field-default-padding-block))
    );
  }

  &.--variant-plain {
    --border-width: 0;
    --text-field-padding-inline: var(--sp-comp-text-field-plain-padding-inline, 0);
    --text-field-padding-block: var(--sp-comp-text-field-plain-padding-block, 0);
  }
}

.sp-text-field-content {
  box-sizing: border-box;
  position: relative;
  display: grid;
  grid-template-columns: min-content minmax(0, 1fr) min-content;
  grid-template-areas: "prepend-inner field append-inner";
  align-items: center;
  padding-inline: var(--sp-comp-text-field-content-padding-inline, 0);
  padding-block: var(--sp-comp-text-field-content-padding-block, 0);
  height: 100%;
}

.sp-text-field-prepend {
  box-sizing: border-box;
  grid-area: prepend-inner;
  min-height: 100%;
}

.sp-text-field-append-container {
  box-sizing: border-box;
  grid-area: append-inner;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  min-height: 100%;
}

.sp-text-field-append,
.sp-text-field-clear {
  min-height: 100%;
}

.sp-text-field-prepend,
.sp-text-field-append {
  box-sizing: border-box;
  display: flex;
  align-items: center;
  justify-content: center;
}

.sp-text-field-content__input-container {
  box-sizing: border-box;
  display: flex;
  flex: 1;
  align-items: center;
  position: relative;
  cursor: var(--sp-comp-text-field-cursor, text);
  min-height: 100%;
}

.sp-text-field-content__input-field {
  box-sizing: border-box;
  padding-inline: var(--text-field-padding-inline);
  padding-block: var(--text-field-padding-block);
  display: flex;
  flex: 1;
  border: none;
  outline: none;
  font-family: var(--sp-comp-text-field-font, var(--sp-sys-form-field-font, inherit));
  font-size: var(--sp-comp-text-field-font-size, var(--sp-sys-form-field-font-size, 1rem));
  background-color: transparent;
  width: 100%;
  min-width: var(--sp-comp-text-field-min-width, 10ch);
  min-height: 100%;
  width: var(--sp-comp-text-field-width, 100%);
  color: var(--sp-comp-text-field-color, var(--sp-sys-form-field-color, inherit));
  cursor: var(--sp-comp-text-field-cursor, text);

  &::placeholder {
    color: var(--sp-comp-text-field-placeholder-color, var(--sp-sys-form-field-placeholder-color, inherit));
  }

  &[inputmode="none"] {
    cursor: pointer;
  }

  &[readonly] {
    text-overflow: ellipsis;
    white-space: nowrap;
    overflow: hidden;
  }

  &:focus {
    border: none !important;
    box-shadow: none !important;
    outline: none !important;
  }
}

.sp-text-field__loader {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
}
</style>
